import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { StorageService } from 'src/app/common/services/storage/storage.service';

// Minimal loader type provided by CKEditor 5 FileRepository.
export interface CkLoader {
  file: Promise<File>;
  // Progress fields are added by the adapter during upload.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Minimal FileRepository surface we need to assign createUploadAdapter.
export interface FileRepositoryWithAdapter {
  createUploadAdapter: (loader: CkLoader) => unknown;
}

// Upload adapter that posts files to our backend and resolves with a URL
// so CKEditor inserts the image when upload completes.
export class CkUploadAdapter {
  private loader: CkLoader;

  private xhr?: XMLHttpRequest;

  constructor(loader: CkLoader) {
    this.loader = loader;
  }

  upload(): Promise<{ default: string }> {
    return this.loader.file.then(
      (file) =>
        new Promise((resolve, reject) => {
          this.initRequest();
          this.initListeners(resolve, reject, file);
          this.sendRequest(file);
        })
    );
  }

  abort(): void {
    if (this.xhr) {
      this.xhr.abort();
    }
  }

  private initRequest(): void {
    const url = `${EnvironmentService.apiUrl()}/uploads`;
    const token = StorageService.getAuthToken()?.token;

    this.xhr = new XMLHttpRequest();
    this.xhr.open('POST', url, true);
    if (token) {
      this.xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    this.xhr.responseType = 'json';
  }

  private initListeners(
    resolve: (value: { default: string }) => void,
    reject: (reason?: unknown) => void,
    file: File
  ): void {
    if (!this.xhr) return;

    const genericError = `Couldn't upload file: ${file.name}.`;

    this.xhr.addEventListener('error', () => reject(genericError));
    this.xhr.addEventListener('abort', () => reject());
    this.xhr.addEventListener('load', () => {
      if (!this.xhr) {
        reject(genericError);
        return;
      }

      const status = this.xhr.status;
      const response = this.xhr.response as {
        location?: string;
        url?: string;
        error?: string;
      } | null;

      if (status < 200 || status >= 300 || !response) {
        reject(response?.error || genericError);
        return;
      }

      const path = response.url || response.location;
      if (!path) {
        reject('Upload succeeded but no file URL returned.');
        return;
      }

      const absolute = path.startsWith('http')
        ? path
        : `${EnvironmentService.apiUrl()}${path}`;

      resolve({ default: absolute });
    });

    if (this.xhr.upload) {
      this.xhr.upload.addEventListener('progress', (evt) => {
        if (evt.lengthComputable) {
          this.loader['uploadTotal'] = evt.total;
          this.loader['uploaded'] = evt.loaded;
        }
      });
    }
  }

  private sendRequest(file: File): void {
    if (!this.xhr) return;
    const data = new FormData();
    data.append('file', file);
    this.xhr.send(data);
  }
}
